import React, { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isWeekend, isSameDay, isAfter, endOfDay } from 'date-fns';
import { Calendar, CheckSquare, Copy, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MonthPicker } from '@/components/ui/month-picker';
import { Label } from '@/components/ui/label';
import { useShiftCopying } from '@/hooks/scheduling/useShiftCopying';
import { DayShift } from '@/types';

interface AdvancedShiftCopyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dayShifts: DayShift[];
  selectedDate: Date | null;
  businessId: string | null;
  onShiftCreated: () => void;
}

export const AdvancedShiftCopyDialog: React.FC<AdvancedShiftCopyDialogProps> = ({
  open,
  onOpenChange,
  dayShifts,
  selectedDate,
  businessId,
  onShiftCreated,
}) => {
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [activeTab, setActiveTab] = useState('quick');
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([]);
  const [targetMonth, setTargetMonth] = useState<Date>(selectedDate || new Date());

  const {
    copyShiftsToAllDaysInMonth,
    copyShiftsToWeekdaysInMonth,
    copyShiftsToSelectedDates,
    isCopying,
    copyingType,
  } = useShiftCopying({ businessId, onShiftCreated });

  // Calculate available dates for the target month (only future dates)
  const availableDates = useMemo(() => {
    if (!targetMonth) return [];
    
    const start = startOfMonth(targetMonth);
    const end = endOfMonth(targetMonth);
    const allDates = eachDayOfInterval({ start, end });
    const today = new Date();
    
    // Exclude the currently selected date and past dates
    return allDates.filter(date => 
      !isSameDay(date, selectedDate) && isAfter(date, endOfDay(today))
    );
  }, [targetMonth, selectedDate]);

  // Quick preset functions
  const selectAllDates = () => {
    setSelectedDates([...availableDates]);
  };

  const selectWeekdays = () => {
    const weekdays = availableDates.filter(date => !isWeekend(date));
    setSelectedDates(weekdays);
  };

  const selectWeekends = () => {
    const weekends = availableDates.filter(date => isWeekend(date));
    setSelectedDates(weekends);
  };

  const selectSpecificWeekday = (weekday: number) => {
    const specificDates = availableDates.filter(date => getDay(date) === weekday);
    setSelectedDates(specificDates);
  };

  // Toggle weekday selection for multi-select
  const toggleWeekday = (weekday: number) => {
    setSelectedWeekdays(prev => {
      if (prev.includes(weekday)) {
        return prev.filter(w => w !== weekday);
      } else {
        return [...prev, weekday];
      }
    });
  };

  // Get combined dates for selected weekdays
  const getSelectedWeekdayDates = () => {
    return availableDates.filter(date => selectedWeekdays.includes(getDay(date)));
  };

  const toggleDate = (date: Date) => {
    setSelectedDates(prev => {
      const isSelected = prev.some(d => isSameDay(d, date));
      if (isSelected) {
        return prev.filter(d => !isSameDay(d, date));
      } else {
        return [...prev, date];
      }
    });
  };

  const clearSelection = () => {
    setSelectedDates([]);
    setSelectedWeekdays([]);
  };

  const handleQuickCopy = async (type: string, value?: string) => {
    if (!selectedDate) return;

    try {
      switch (type) {
        case 'all':
          await copyShiftsToAllDaysInMonth(targetMonth, dayShifts);
          break;
        case 'weekday':
          if (value) {
            await copyShiftsToWeekdaysInMonth(targetMonth, dayShifts, value);
          }
          break;
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Quick copy failed:', error);
    }
  };

  const handleMultiWeekdayCopy = async () => {
    if (selectedWeekdays.length === 0) return;

    try {
      const datesToCopy = getSelectedWeekdayDates();
      await copyShiftsToSelectedDates(datesToCopy, dayShifts);
      onOpenChange(false);
      setSelectedWeekdays([]);
    } catch (error) {
      console.error('Multi-weekday copy failed:', error);
    }
  };

  const handleCustomCopy = async () => {
    if (selectedDates.length === 0) return;

    try {
      await copyShiftsToSelectedDates(selectedDates, dayShifts);
      onOpenChange(false);
      clearSelection();
    } catch (error) {
      console.error('Custom copy failed:', error);
    }
  };

  const handleClose = () => {
    // Prevent closing while copying is in progress
    if (isCopying) return;

    onOpenChange(false);
    clearSelection();
    setActiveTab('quick');
    setSelectedWeekdays([]);
    setTargetMonth(selectedDate || new Date());
  };

  if (!selectedDate) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCopying ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Copy className="h-5 w-5" />
            )}
            Copy Shifts from {format(selectedDate, 'MMM d, yyyy')}
          </DialogTitle>
          <div className="space-y-1">
            {isCopying ? (
              <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                {copyingType === 'all-days' && `Copying shifts to all days in ${format(targetMonth, 'MMMM yyyy')}...`}
                {copyingType === 'weekdays' && `Copying shifts to selected weekdays...`}
                {copyingType === 'selected-dates' && `Copying shifts to selected dates...`}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Copy {dayShifts.length} shift{dayShifts.length !== 1 ? 's' : ''} to other dates
              </p>
            )}
            {!isCopying && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <span className="text-amber-600 dark:text-amber-400">⚠</span>
                Warning: This will replace any existing shifts on the selected dates
              </p>
            )}
          </div>
        </DialogHeader>

        {/* Month/Year Picker */}
        <div className="flex items-center gap-2 mb-4 flex-shrink-0">
          <span className="text-sm font-medium">Target month:</span>
          <MonthPicker
            value={targetMonth}
            onChange={setTargetMonth}
            disabled={isCopying}
            size="sm"
            showTodayButton={false}
          />
        </div>

        <Tabs value={activeTab} onValueChange={isCopying ? undefined : setActiveTab} className="w-full flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
            <TabsTrigger value="quick" disabled={isCopying}>Quick Copy</TabsTrigger>
            <TabsTrigger value="custom" disabled={isCopying}>Custom Selection</TabsTrigger>
          </TabsList>

          <TabsContent value="quick" className="space-y-4 flex-1 overflow-y-auto">
            {availableDates.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-center text-muted-foreground">
                <div>
                  <p className="text-sm">No future dates available in {format(targetMonth, 'MMMM yyyy')}</p>
                  <p className="text-xs mt-1">All remaining days are in the past</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Copy to all days:</h4>
                  <Button
                    onClick={() => handleQuickCopy('all')}
                    className="w-full justify-start"
                    variant="outline"
                    disabled={isCopying}
                  >
                    {isCopying && copyingType === 'all-days' ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Calendar className="h-4 w-4 mr-2" />
                    )}
                    {isCopying && copyingType === 'all-days' 
                      ? 'Copying...' 
                      : `All future days in ${format(targetMonth, 'MMMM yyyy')}`
                    }
                  </Button>
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-2">Copy to specific weekdays:</h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { day: 'Future Mondays', value: 1 },
                        { day: 'Future Tuesdays', value: 2 },
                        { day: 'Future Wednesdays', value: 3 },
                        { day: 'Future Thursdays', value: 4 },
                        { day: 'Future Fridays', value: 5 },
                        { day: 'Future Saturdays', value: 6 },
                        { day: 'Future Sundays', value: 0 }
                      ].map(({ day, value }) => (
                        <Button
                          key={value}
                          variant={selectedWeekdays.includes(value) ? "default" : "outline"}
                          className="justify-start"
                          onClick={() => toggleWeekday(value)}
                          disabled={isCopying || availableDates.filter(date => getDay(date) === value).length === 0}
                        >
                          {day}
                        </Button>
                      ))}
                    </div>
                    {selectedWeekdays.length > 0 && (
                      <Button
                        onClick={handleMultiWeekdayCopy}
                        className="w-full"
                        size="sm"
                        disabled={isCopying}
                      >
                        {isCopying && copyingType === 'weekdays' ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Copying...
                          </>
                        ) : (
                          `Copy to ${getSelectedWeekdayDates().length} selected weekday date${getSelectedWeekdayDates().length !== 1 ? 's' : ''}`
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="custom" className="space-y-4 flex-1 overflow-y-auto">
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium mb-2">Quick selections:</h4>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    onClick={selectAllDates} 
                    size="sm" 
                    variant="outline"
                    disabled={isCopying || availableDates.length === 0}
                  >
                    Select All
                  </Button>
                  <Button 
                    onClick={selectWeekdays} 
                    size="sm" 
                    variant="outline"
                    disabled={isCopying || availableDates.filter(date => !isWeekend(date)).length === 0}
                  >
                    Weekdays Only
                  </Button>
                  <Button 
                    onClick={selectWeekends} 
                    size="sm" 
                    variant="outline"
                    disabled={isCopying || availableDates.filter(date => isWeekend(date)).length === 0}
                  >
                    Weekends Only
                  </Button>
                  <Button 
                    onClick={clearSelection} 
                    size="sm" 
                    variant="outline"
                    disabled={isCopying || selectedDates.length === 0}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-2">
                  Select specific dates: 
                  {selectedDates.length > 0 && (
                    <Badge className="ml-2">
                      {selectedDates.length} selected
                    </Badge>
                  )}
                </h4>
                
                <ScrollArea className="h-[200px] w-full border rounded-md p-4">
                  {availableDates.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-center text-muted-foreground">
                      <div>
                        <p className="text-sm">No future dates available in {format(selectedDate, 'MMMM yyyy')}</p>
                        <p className="text-xs mt-1">All remaining days are in the past</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium mb-2">
                        <div>Sun</div>
                        <div>Mon</div>
                        <div>Tue</div>
                        <div>Wed</div>
                        <div>Thu</div>
                        <div>Fri</div>
                        <div>Sat</div>
                      </div>
                      
                      <div className="grid grid-cols-7 gap-1">
                        {/* Add empty cells for days before month start */}
                        {Array.from({ length: getDay(startOfMonth(targetMonth)) }).map((_, index) => (
                          <div key={`empty-${index}`} className="h-8" />
                        ))}
                        
                        {/* Show all dates in month but disable past ones */}
                        {eachDayOfInterval({ 
                          start: startOfMonth(targetMonth), 
                          end: endOfMonth(targetMonth) 
                        }).map(date => {
                          const isSelected = selectedDates.some(d => isSameDay(d, date));
                          const isCurrentDate = isSameDay(date, selectedDate);
                          const isPastDate = !isAfter(date, endOfDay(new Date())) && !isCurrentDate;
                          const isAvailable = availableDates.some(d => isSameDay(d, date));
                          
                          return (
                            <button
                              key={date.toISOString()}
                              onClick={() => (isAvailable && !isCopying) ? toggleDate(date) : undefined}
                              disabled={!isAvailable || isCopying}
                              className={`
                                h-8 w-8 text-xs rounded-md flex items-center justify-center
                                transition-colors duration-150
                                ${isCurrentDate
                                  ? 'bg-blue-100 text-blue-800 border border-blue-300 cursor-not-allowed'
                                  : isPastDate
                                    ? 'text-muted-foreground/50 cursor-not-allowed'
                                    : isSelected
                                      ? 'bg-primary text-primary-foreground'
                                      : isAvailable
                                        ? 'hover:bg-muted'
                                        : 'cursor-not-allowed'
                                }
                              `}
                              title={
                                isCurrentDate 
                                  ? 'Source date (cannot copy to itself)'
                                  : isPastDate 
                                    ? 'Past date (cannot copy to past)'
                                    : undefined
                              }
                            >
                              {format(date, 'd')}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </ScrollArea>
              </div>

              {selectedDates.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Selected dates:</h4>
                  <ScrollArea className="h-16 w-full">
                    <div className="flex flex-wrap gap-1">
                      {selectedDates
                        .sort((a, b) => a.getTime() - b.getTime())
                        .map(date => (
                          <Badge key={date.toISOString()} variant="secondary" className="text-xs">
                            {format(date, 'MMM d')}
                            <button
                              onClick={() => toggleDate(date)}
                              className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 flex-shrink-0 border-t pt-4 mt-4">
          <div className="flex-1 text-xs text-muted-foreground mr-2">
            {activeTab === 'custom' && selectedDates.length > 0 && !isCopying && (
              <p className="text-amber-600 dark:text-amber-400">
                ⚠ Existing shifts on selected dates will be replaced
              </p>
            )}
          </div>
          <Button variant="outline" onClick={handleClose} disabled={isCopying}>
            Cancel
          </Button>
          {activeTab === 'custom' && (
            <Button
              onClick={handleCustomCopy}
              disabled={isCopying || selectedDates.length === 0}
              className="flex items-center gap-2"
            >
              {isCopying && copyingType === 'selected-dates' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Copying...
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy to {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};